import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconButtonComponent, TooltipDirective } from '@mode/capra';
import { generateGradientStepColors } from '@mode/charts/util-vegas';
import { OrgTypes } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { ColorSwatchFlyoutComponent } from '../color-swatch-flyout/color-swatch-flyout.component';

export interface GradientLabel {
  label: string;
  isAuto: boolean;
}

export interface GradientValueLabels {
  min: GradientLabel | undefined;
  mid: GradientLabel | undefined;
  max: GradientLabel | undefined;
}

export type SequentialDivergentColorPosition = 'min' | 'mid' | 'max';
@Component({
  standalone: true,
  selector: 'mode-sequential-divergent-color-picker',
  imports: [CommonModule, ColorSwatchFlyoutComponent, IconButtonComponent, TooltipDirective],
  templateUrl: './sequential-divergent-color-picker.component.html',
  styleUrls: ['./sequential-divergent-color-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SequentialDivergentColorPickerComponent implements OnChanges {
  @Input() selectedColorPalette: OrgTypes.ColorPalette | undefined;
  @Input() flyoutDefaultPalette: OrgTypes.ColorPalette | undefined;
  @Input() allColorPalettes: OrgTypes.ColorPalette[] = []; // this is needed for the swatch + flyout
  @Input() labels: GradientValueLabels | undefined;
  @Input() vizToken = '';
  @Input() numberOfSteps = 0;
  @Input() hideSteps = false;
  @Input() hideLabels = false;
  @Input() hideAutoLabel = false;
  @Input() isBinned = false;
  @Output() colorChanged = new EventEmitter<string[]>();
  @Output() stepChanged = new EventEmitter<{ palette: string[]; numberOfSteps: number }>();

  constructor(private analyticsService: AnalyticsService) {}

  minColor = '';
  midColor = '';
  maxColor = '';
  palette: string[] = [];
  hasSteps = false;
  stepColors: string[] = [];

  isDivergent = false;
  background = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedColorPalette']?.currentValue) {
      this.isDivergent = this.selectedColorPalette?.type === OrgTypes.ColorPaletteType.Divergent;
      this.setColorsFromSelectedPalette();
      this.setPalette();
    }

    if (changes['numberOfSteps']?.currentValue || changes['numberOfSteps']?.currentValue === 0) {
      this.hasSteps = this.numberOfSteps > 0;
      this.setColorsFromSelectedPalette();
      this.setPalette();
    }
  }

  setColorsFromSelectedPalette() {
    if (this.isDivergent) {
      const colors = this.selectedColorPalette?.value
        .split(',')
        .slice(0, 3)
        .map((color) => color.trim());
      if (colors) {
        [this.minColor, this.midColor, this.maxColor] = colors;
        this.stepColors = generateGradientStepColors(colors, this.numberOfSteps);
      }
    } else {
      const colors = this.selectedColorPalette?.value
        .split(',')
        .slice(0, 2)
        .map((color) => color.trim());
      if (colors) {
        [this.minColor, this.maxColor] = colors;
        this.stepColors = generateGradientStepColors(colors, this.numberOfSteps);
      }
    }
  }

  setPalette() {
    if (this.isDivergent) {
      this.palette = [this.minColor, this.midColor, this.maxColor];
    } else {
      this.palette = [this.minColor, this.maxColor];
    }

    if (!this.hasSteps) {
      this.background = `linear-gradient(to right, ${this.palette})`;
    }
  }

  onColorChanged(
    event: { colorSelected: string; selectedPalette: OrgTypes.ColorPalette },
    position: SequentialDivergentColorPosition
  ) {
    let colorChanged = false;
    switch (position) {
      case 'min':
        if (event.colorSelected.trim() !== this.minColor) {
          this.minColor = event.colorSelected.trim();
          colorChanged = true;
        }
        break;
      case 'mid':
        if (event.colorSelected.trim() !== this.midColor) {
          this.midColor = event.colorSelected.trim();
          colorChanged = true;
        }
        break;
      case 'max':
        if (event.colorSelected.trim() !== this.maxColor) {
          this.maxColor = event.colorSelected.trim();
          colorChanged = true;
        }
        break;
    }
    if (colorChanged) {
      if (event.selectedPalette !== this.flyoutDefaultPalette) {
        this.flyoutDefaultPalette = event.selectedPalette;
      }

      this.setPalette();
      this.colorChanged.emit(this.palette);
    }
  }

  reverseGradient() {
    const minColor = this.minColor;
    this.minColor = this.maxColor;
    this.maxColor = minColor;
    this.setPalette();
    this.colorChanged.emit(this.palette);

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.EditColorsModal,
      action: 'Reverse gradient',
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
    });
  }

  addStep() {
    if (this.numberOfSteps === 7) {
      // This should not be possible since it is disabled in the UI here
      return;
    }
    if (this.numberOfSteps === 0) {
      // Update steps to 2 since it's impossible to have 1 step
      this.numberOfSteps = 2;
    } else {
      this.numberOfSteps = this.numberOfSteps + 1;
    }

    this.hasSteps = true;
    this.setPalette();
    this.stepColors = generateGradientStepColors(this.palette, this.numberOfSteps);
    this.stepChanged.emit({ palette: this.palette, numberOfSteps: this.numberOfSteps });

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.EditColorsModal,
      action: 'Add Steps',
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
      status: `step count: ${this.numberOfSteps}`,
    });
  }

  removeStep() {
    if (this.numberOfSteps === 0) {
      // This should not be possible since it is disabled in the UI here
      return;
    }
    if (this.numberOfSteps === 2) {
      // When we're on the last step (Step 2), reset steps back to 0
      this.numberOfSteps = 0;
      this.hasSteps = false;
    } else {
      this.numberOfSteps = this.numberOfSteps - 1;
    }

    this.setPalette();
    this.stepColors = generateGradientStepColors(this.palette, this.numberOfSteps);
    this.stepChanged.emit({ palette: this.palette, numberOfSteps: this.numberOfSteps });

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.EditColorsModal,
      action: 'Remove Steps',
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
      status: `step count: ${this.numberOfSteps}`,
    });
  }
}
